import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  DocuSignTemplate,
  DocuSignBaseTab,
  DocuSignTextTab,
  DocuSignCheckboxTab,
  DocuSignSignHereTab,
} from '../types';
import { DocumensoExportPanel } from './DocumensoExportPanel';
import type { ConversionResult } from '../types/documenso';
import type { ReplacementVariable, EventData } from '../utils/documensoConverter';

const DRAWER_WIDTH = 320;

// Reusable section wrapper
const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
      {label}
    </Typography>
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
      {children}
    </Box>
  </Box>
);

// Compact toggle row
const ToggleRow: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25 }}>
    <Typography variant="body2">{label}</Typography>
    <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </Box>
);

interface FieldPropertyPanelProps {
  template: DocuSignTemplate | null;
  selectedField: {
    tabType: string;
    tabIndex: number;
    signerIndex: number;
  } | null;
  onFieldUpdate: (
    signerIndex: number,
    tabType: string,
    tabIndex: number,
    updates: Partial<DocuSignBaseTab>
  ) => void;
  onAddRadioValue?: (groupIndex: number, value: string) => void;
  onRemoveRadioValue?: (groupIndex: number, radioIndex: number) => void;
  documensoConversion?: ConversionResult;
  onReplacementVariablesChange?: (variables: ReplacementVariable[] | undefined) => void;
  onEventDataChange?: (eventData: EventData | undefined) => void;
  templateId: string;
  version: number;
  isOpen: boolean;
  onToggle: () => void;
}

const drawerPaperSx = {
  width: DRAWER_WIDTH,
  boxSizing: 'border-box',
  top: { xs: 56, sm: 64 },
  height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
  borderLeft: '1px solid',
  borderColor: 'divider',
};

const FieldPropertyPanel: React.FC<FieldPropertyPanelProps> = ({
  template,
  selectedField,
  onFieldUpdate,
  onAddRadioValue,
  onRemoveRadioValue,
  documensoConversion,
  onReplacementVariablesChange,
  onEventDataChange,
  templateId,
  version,
  isOpen,
  onToggle,
}) => {
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);

  const currentField = useMemo(() => {
    if (!template || !selectedField) return null;
    const signer = template.recipients.signers[selectedField.signerIndex];
    if (!signer) return null;
    const tabs = signer.tabs[selectedField.tabType as keyof typeof signer.tabs];
    if (!tabs || !Array.isArray(tabs)) return null;
    return tabs[selectedField.tabIndex];
  }, [template, selectedField]);

  useEffect(() => {
    if (currentField && selectedField?.tabType !== 'radioGroupTabs') {
      const field = currentField as DocuSignBaseTab;
      setLocalValues({
        tabLabel: field.tabLabel || '',
        pageNumber: field.pageNumber || '1',
        xPosition: field.xPosition || '0',
        yPosition: field.yPosition || '0',
        width: field.width || '100',
        height: field.height || '20',
        fontSize: (field as DocuSignTextTab).fontSize || '12',
        fontColor: (field as DocuSignTextTab).fontColor || '#000000',
        required: (field as DocuSignTextTab).required || 'false',
        locked: (field as DocuSignTextTab).locked || 'false',
        bold: (field as any).bold || 'false',
        italic: (field as any).italic || 'false',
        underline: (field as any).underline || 'false',
        value: (field as DocuSignTextTab).value || '',
        optional: (field as DocuSignSignHereTab).optional || 'false',
        scaleValue: (field as DocuSignSignHereTab).scaleValue || '1',
        stampType: (field as DocuSignSignHereTab).stampType || 'signature',
        selected: (field as DocuSignCheckboxTab).selected || 'false',
      });
      setValidationError(null);
    }
  }, [currentField, selectedField]);

  const handleChange = (field: string, value: string) => {
    setLocalValues((prev) => ({ ...prev, [field]: value }));
    if (field === 'tabLabel' && !value.trim()) {
      setValidationError('Tab label cannot be empty.');
      return;
    }
    if (field === 'tabLabel') setValidationError(null);
    if (selectedField) {
      onFieldUpdate(selectedField.signerIndex, selectedField.tabType, selectedField.tabIndex, { [field]: value } as Partial<DocuSignBaseTab>);
    }
  };

  const updateRadioField = (updates: any) => {
    if (selectedField) {
      onFieldUpdate(selectedField.signerIndex, selectedField.tabType, selectedField.tabIndex, updates);
    }
  };

  const showTextProperties = selectedField?.tabType === 'textTabs';
  const showCheckboxProperties = selectedField?.tabType === 'checkboxTabs';
  const showSignatureProperties = selectedField?.tabType === 'signHereTabs';
  const showDateProperties = selectedField?.tabType === 'dateSignedTabs';
  const showRadioGroupProperties = selectedField?.tabType === 'radioGroupTabs';

  const FIELD_TYPE_LABELS: Record<string, string> = {
    textTabs: 'Text Field',
    checkboxTabs: 'Checkbox',
    signHereTabs: 'Signature',
    dateSignedTabs: 'Date Signed',
    radioGroupTabs: 'Radio Group',
    numberTabs: 'Number',
    emailTabs: 'Email',
    dateTabs: 'Date',
    listTabs: 'Dropdown',
    initialHereTabs: 'Initials',
    fullNameTabs: 'Full Name',
    titleTabs: 'Title',
    companyTabs: 'Company',
  };

  // Toggle button (shared between both states)
  const toggleButton = !isOpen ? (
    <IconButton
      onClick={onToggle}
      sx={{
        position: 'fixed', right: 0, top: '50%', transform: 'translateY(-50%)',
        zIndex: (theme) => theme.zIndex.drawer - 1,
        bgcolor: 'primary.main', color: 'white', borderRadius: '8px 0 0 8px',
        '&:hover': { bgcolor: 'primary.dark' },
      }}
    >
      <ChevronLeftIcon />
    </IconButton>
  ) : null;

  // Empty state (no field selected)
  if (!selectedField || !currentField) {
    return (
      <>
        {toggleButton}
        <Drawer variant="persistent" anchor="right" open={isOpen} sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': drawerPaperSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>Properties</Typography>
              <IconButton onClick={onToggle} size="small"><ChevronRightIcon /></IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: '#fafafa' }}>
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Select a field to edit its properties</Typography>
              </Box>
              {documensoConversion && (
                <Box sx={{ p: 2 }}>
                  <DocumensoExportPanel conversionResult={documensoConversion} onReplacementVariablesChange={onReplacementVariablesChange} onEventDataChange={onEventDataChange} templateId={templateId} version={version} />
                </Box>
              )}
            </Box>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <>
      {toggleButton}
      <Drawer variant="persistent" anchor="right" open={isOpen} sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': drawerPaperSx }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header with field type badge */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>Properties</Typography>
              <Chip label={FIELD_TYPE_LABELS[selectedField.tabType] || selectedField.tabType} size="small" color="primary" variant="outlined" sx={{ borderRadius: 1.5, height: 22, fontSize: '0.7rem' }} />
            </Stack>
            <IconButton onClick={onToggle} size="small"><ChevronRightIcon /></IconButton>
          </Box>

          {validationError && (
            <Box sx={{ px: 2, pt: 1.5 }}>
              <Alert severity="error" onClose={() => setValidationError(null)} sx={{ py: 0 }}>{validationError}</Alert>
            </Box>
          )}

          {/* Scrollable content */}
          <Box key={`${selectedField.signerIndex}-${selectedField.tabType}-${selectedField.tabIndex}`} sx={{ flexGrow: 1, overflow: 'auto', p: 2, bgcolor: '#fafafa' }}>
            {/* Identity */}
            <Section label="Identity">
              <TextField fullWidth label="Tab Label" value={localValues.tabLabel || ''} onChange={(e) => handleChange('tabLabel', e.target.value)} size="small" error={!!validationError} variant="outlined" sx={{ mb: (showTextProperties || showDateProperties) ? 1.5 : 0 }} />
              {(showTextProperties || showDateProperties) && (
                <TextField fullWidth label="Default Value" value={localValues.value || ''} onChange={(e) => handleChange('value', e.target.value)} size="small" placeholder="Pre-filled value" multiline minRows={2} maxRows={6} />
              )}
            </Section>

            {/* Position & Size — compact 2x2 grid */}
            <Section label="Position & Size">
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                <TextField label="X" type="number" value={localValues.xPosition || '0'} onChange={(e) => handleChange('xPosition', e.target.value)} size="small" inputProps={{ min: 0, step: 1 }} />
                <TextField label="Y" type="number" value={localValues.yPosition || '0'} onChange={(e) => handleChange('yPosition', e.target.value)} size="small" inputProps={{ min: 0, step: 1 }} />
                <TextField label="W" type="number" value={localValues.width || '100'} onChange={(e) => handleChange('width', e.target.value)} size="small" inputProps={{ min: 1, step: 1 }} />
                <TextField label="H" type="number" value={localValues.height || '20'} onChange={(e) => handleChange('height', e.target.value)} size="small" inputProps={{ min: 1, step: 1 }} />
              </Box>
              <TextField fullWidth label="Page" type="number" value={localValues.pageNumber || '1'} onChange={(e) => handleChange('pageNumber', e.target.value)} size="small" inputProps={{ min: 1 }} />
            </Section>

            {/* Text formatting */}
            {(showTextProperties || showDateProperties) && (
              <Section label="Text Formatting">
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
                  <TextField label="Size" type="number" value={localValues.fontSize || '12'} onChange={(e) => handleChange('fontSize', e.target.value)} size="small" inputProps={{ min: 6, max: 72, step: 1 }} />
                  <TextField label="Color" type="color" value={localValues.fontColor || '#000000'} onChange={(e) => handleChange('fontColor', e.target.value)} size="small" />
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {(['bold', 'italic', 'underline'] as const).map((style) => (
                    <Chip
                      key={style}
                      label={style.charAt(0).toUpperCase() + style.slice(1)}
                      size="small"
                      variant={localValues[style] === 'true' ? 'filled' : 'outlined'}
                      color={localValues[style] === 'true' ? 'primary' : 'default'}
                      onClick={() => handleChange(style, localValues[style] === 'true' ? 'false' : 'true')}
                      sx={{ cursor: 'pointer', borderRadius: 1.5, fontWeight: style === 'bold' ? 700 : 400, fontStyle: style === 'italic' ? 'italic' : 'normal', textDecoration: style === 'underline' ? 'underline' : 'none' }}
                    />
                  ))}
                </Stack>
              </Section>
            )}

            {/* Signature settings */}
            {showSignatureProperties && (
              <Section label="Signature">
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <InputLabel>Stamp Type</InputLabel>
                  <Select value={localValues.stampType || 'signature'} onChange={(e) => handleChange('stampType', e.target.value)} label="Stamp Type">
                    <MenuItem value="signature">Signature</MenuItem>
                    <MenuItem value="stamp">Stamp</MenuItem>
                  </Select>
                </FormControl>
                <TextField fullWidth label="Scale" type="number" value={localValues.scaleValue || '1'} onChange={(e) => handleChange('scaleValue', e.target.value)} size="small" inputProps={{ min: 0.1, max: 2, step: 0.1 }} sx={{ mb: 0.5 }} />
                <ToggleRow label="Optional" checked={localValues.optional === 'true'} onChange={(v) => handleChange('optional', v ? 'true' : 'false')} />
              </Section>
            )}

            {/* Checkbox settings */}
            {showCheckboxProperties && (
              <Section label="Checkbox">
                <ToggleRow label="Pre-selected" checked={localValues.selected === 'true'} onChange={(v) => handleChange('selected', v ? 'true' : 'false')} />
              </Section>
            )}

            {/* Radio group */}
            {showRadioGroupProperties && currentField && (
              <>
                <Section label="Radio Group">
                  <TextField fullWidth label="Group Name" value={(currentField as any).groupName || ''} onChange={(e) => updateRadioField({ groupName: e.target.value })} size="small" sx={{ mb: 1.5 }} />

                  {(currentField as any).radios?.map((radio: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                        transition: 'border-color 0.15s',
                        '&:hover': { borderColor: 'primary.light' },
                      }}
                    >
                      {/* Option header row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, bgcolor: '#f8f9fa', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ flexGrow: 1 }}>
                          Option {index + 1}
                        </Typography>
                        <IconButton size="small" onClick={() => onRemoveRadioValue?.(selectedField.tabIndex, index)} disabled={(currentField as any).radios?.length <= 1} sx={{ p: 0.25 }}>
                          <CancelIcon sx={{ fontSize: 16, color: (currentField as any).radios?.length <= 1 ? 'action.disabled' : 'error.main' }} />
                        </IconButton>
                      </Box>
                      {/* Option body */}
                      <Box sx={{ p: 1.5 }}>
                        <TextField
                          fullWidth size="small" value={radio.value} label="Label"
                          onChange={(e) => {
                            if (selectedField && template) {
                              const updatedRadios = [...(currentField as any).radios];
                              updatedRadios[index] = { ...updatedRadios[index], value: e.target.value };
                              updateRadioField({ radios: updatedRadios });
                            }
                          }}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                          <TextField
                            size="small" label="X" type="number" value={radio.xPosition || '0'}
                            inputProps={{ min: 0, step: 1 }}
                            onChange={(e) => {
                              if (selectedField && template) {
                                const updatedRadios = [...(currentField as any).radios];
                                updatedRadios[index] = { ...updatedRadios[index], xPosition: e.target.value };
                                updateRadioField({ radios: updatedRadios });
                              }
                            }}
                          />
                          <TextField
                            size="small" label="Y" type="number" value={radio.yPosition || '0'}
                            inputProps={{ min: 0, step: 1 }}
                            onChange={(e) => {
                              if (selectedField && template) {
                                const updatedRadios = [...(currentField as any).radios];
                                updatedRadios[index] = { ...updatedRadios[index], yPosition: e.target.value };
                                updateRadioField({ radios: updatedRadios });
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}

                  <Button
                    size="small" variant="text" fullWidth
                    onClick={() => onAddRadioValue?.(selectedField.tabIndex, `Option ${((currentField as any).radios?.length || 0) + 1}`)}
                    sx={{ mt: 0.5, textTransform: 'none', borderRadius: 2, color: 'primary.main', fontWeight: 500, '&:hover': { bgcolor: 'primary.50' } }}
                  >
                    + Add Option
                  </Button>
                </Section>

                <Box sx={{ mb: 2 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      const el = document.getElementById('radio-group-advanced');
                      if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                    }}
                    sx={{ textTransform: 'none', color: 'text.secondary', px: 0, fontSize: '0.75rem' }}
                  >
                    Advanced DocuSign settings ▸
                  </Button>
                  <Box id="radio-group-advanced" sx={{ display: 'none' }}>
                    <Section label="Group Settings (DocuSign)">
                      <ToggleRow label="Template Locked" checked={(currentField as any).templateLocked === 'true'} onChange={(v) => updateRadioField({ templateLocked: v ? 'true' : 'false' })} />
                      <ToggleRow label="Template Required" checked={(currentField as any).templateRequired === 'true'} onChange={(v) => updateRadioField({ templateRequired: v ? 'true' : 'false' })} />
                      <ToggleRow label="Require All" checked={(currentField as any).requireAll === 'true'} onChange={(v) => updateRadioField({ requireAll: v ? 'true' : 'false' })} />
                      <ToggleRow label="Shared" checked={(currentField as any).shared === 'true'} onChange={(v) => updateRadioField({ shared: v ? 'true' : 'false' })} />
                    </Section>
                  </Box>
                </Box>

                {(currentField as any).radios && (currentField as any).radios.length > 0 && (
                  <Section label="Radio Button Options">
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel>Pre-selected</InputLabel>
                      <Select
                        value={(currentField as any).radios.findIndex((r: any) => r.selected === 'true').toString()}
                        onChange={(e) => {
                          if (template) {
                            const updatedRadios = (currentField as any).radios.map((r: any, idx: number) => ({ ...r, selected: idx === parseInt(e.target.value) ? 'true' : 'false' }));
                            updateRadioField({ radios: updatedRadios });
                          }
                        }}
                        label="Pre-selected"
                      >
                        <MenuItem value="-1">None</MenuItem>
                        {(currentField as any).radios.map((radio: any, idx: number) => (
                          <MenuItem key={idx} value={idx.toString()}>{radio.value}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <ToggleRow label="Required" checked={(currentField as any).radios.every((r: any) => r.required === 'true')} onChange={(v) => { const updatedRadios = (currentField as any).radios.map((r: any) => ({ ...r, required: v ? 'true' : 'false' })); updateRadioField({ radios: updatedRadios }); }} />
                    <ToggleRow label="Locked" checked={(currentField as any).radios.every((r: any) => r.locked === 'true')} onChange={(v) => { const updatedRadios = (currentField as any).radios.map((r: any) => ({ ...r, locked: v ? 'true' : 'false' })); updateRadioField({ radios: updatedRadios }); }} />
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                      {(['bold', 'italic', 'underline'] as const).map((style) => (
                        <Chip
                          key={style}
                          label={style.charAt(0).toUpperCase() + style.slice(1)}
                          size="small"
                          variant={(currentField as any).radios.every((r: any) => r[style] === 'true') ? 'filled' : 'outlined'}
                          color={(currentField as any).radios.every((r: any) => r[style] === 'true') ? 'primary' : 'default'}
                          onClick={() => {
                            const allTrue = (currentField as any).radios.every((r: any) => r[style] === 'true');
                            const updatedRadios = (currentField as any).radios.map((r: any) => ({ ...r, [style]: allTrue ? 'false' : 'true' }));
                            updateRadioField({ radios: updatedRadios });
                          }}
                          sx={{ cursor: 'pointer', borderRadius: 1.5, fontWeight: style === 'bold' ? 700 : 400, fontStyle: style === 'italic' ? 'italic' : 'normal', textDecoration: style === 'underline' ? 'underline' : 'none' }}
                        />
                      ))}
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5 }}>
                      <TextField label="Font" value={(currentField as any).radios[0]?.font || 'lucidaconsole'} onChange={(e) => { const updatedRadios = (currentField as any).radios.map((r: any) => ({ ...r, font: e.target.value })); updateRadioField({ radios: updatedRadios }); }} size="small" />
                      <TextField label="Size" value={(currentField as any).radios[0]?.fontSize || 'size9'} onChange={(e) => { const updatedRadios = (currentField as any).radios.map((r: any) => ({ ...r, fontSize: e.target.value })); updateRadioField({ radios: updatedRadios }); }} size="small" />
                    </Box>
                    <TextField fullWidth label="Color" value={(currentField as any).radios[0]?.fontColor || 'black'} onChange={(e) => { const updatedRadios = (currentField as any).radios.map((r: any) => ({ ...r, fontColor: e.target.value })); updateRadioField({ radios: updatedRadios }); }} size="small" sx={{ mt: 1.5 }} />
                  </Section>
                )}
              </>
            )}

            {/* Field options (common flags) */}
            {(showTextProperties || showDateProperties || showCheckboxProperties) && (
              <Section label="Options">
                <ToggleRow label="Required" checked={localValues.required === 'true'} onChange={(v) => handleChange('required', v ? 'true' : 'false')} />
                <ToggleRow label="Locked" checked={localValues.locked === 'true'} onChange={(v) => handleChange('locked', v ? 'true' : 'false')} />
              </Section>
            )}

            {/* System info — compact */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>System</Typography>
              <Typography variant="caption" color="text.disabled">Doc {(currentField as DocuSignBaseTab).documentId} · Recipient {(currentField as DocuSignBaseTab).recipientId}</Typography>
            </Box>
          </Box>

          {/* Documenso Export Panel */}
          {documensoConversion && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <DocumensoExportPanel conversionResult={documensoConversion} onReplacementVariablesChange={onReplacementVariablesChange} onEventDataChange={onEventDataChange} templateId={templateId} version={version} />
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default FieldPropertyPanel;
